/**
 * Wave 3 Block 3A — RevisionTimeline
 * 
 * Vertical timeline component for authorization state history.
 * Displays comments, state transitions, and ÚCL feedback.
 * 
 * Item 3a: STS Scenarios History
 */
import { useState, useEffect } from 'react';
import { 
  FileText, MessageSquare, Send, Settings, RefreshCw, 
  ChevronDown, ChevronUp, Filter, Clock, User,
  AlertTriangle, CheckCircle, XCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import api from '../../lib/api';
import { format } from 'date-fns';
import { cs, enUS } from 'date-fns/locale';

// Comment type configuration with icons and colors
const COMMENT_TYPES = {
  internal_note: {
    icon: FileText,
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    label: { cs: 'Interní poznámka', en: 'Internal Note' }
  },
  ucl_feedback: {
    icon: MessageSquare,
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    label: { cs: 'Zpětná vazba ÚCL', en: 'ÚCL Feedback' }
  },
  operator_response: {
    icon: Send,
    color: 'bg-green-100 text-green-800 border-green-200',
    label: { cs: 'Odpověď operátora', en: 'Operator Response' }
  },
  system_event: {
    icon: Settings,
    color: 'bg-gray-100 text-gray-600 border-gray-200',
    label: { cs: 'Systémová událost', en: 'System Event' }
  },
  status_transition: {
    icon: RefreshCw,
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    label: { cs: 'Změna stavu', en: 'Status Transition' }
  }
};

// Workflow state labels
const WORKFLOW_STATES = {
  draft: { cs: 'Koncept', en: 'Draft' },
  in_review: { cs: 'V přezkoumání', en: 'In Review' },
  submitted_to_ucl: { cs: 'Odesláno na ÚCL', en: 'Submitted to ÚCL' },
  approved: { cs: 'Schváleno', en: 'Approved' },
  needs_amendment: { cs: 'Vyžaduje úpravy', en: 'Needs Amendment' },
  rejected: { cs: 'Zamítnuto', en: 'Rejected' },
  active: { cs: 'Aktivní', en: 'Active' },
  renewal_due: { cs: 'K obnovení', en: 'Renewal Due' },
  expired: { cs: 'Vypršelo', en: 'Expired' },
  withdrawn: { cs: 'Staženo', en: 'Withdrawn' }
};

// Single revision entry component
const RevisionEntry = ({ revision, language = 'cs', isLast = false }) => {
  const [expanded, setExpanded] = useState(false);
  
  const typeConfig = COMMENT_TYPES[revision.comment_type] || COMMENT_TYPES.system_event;
  const TypeIcon = typeConfig.icon;
  
  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return format(date, 'dd.MM.yyyy HH:mm', { locale: language === 'cs' ? cs : enUS });
    } catch {
      return dateStr;
    }
  };
  
  const shouldShowExpand = revision.comment && revision.comment.length > 200;
  const displayComment = expanded ? revision.comment : revision.comment?.slice(0, 200);
  
  return (
    <div className="relative flex gap-4">
      {/* Timeline line */}
      {!isLast && (
        <div className="absolute left-5 top-10 bottom-0 w-0.5 bg-gray-200" />
      )}
      
      {/* Icon */}
      <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center ${typeConfig.color} border shrink-0`}>
        <TypeIcon className="w-4 h-4" />
      </div>
      
      {/* Content */}
      <div className="flex-1 pb-6">
        <Card className="border shadow-sm">
          <CardContent className="p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-400" />
                <span className="font-medium text-sm">{revision.created_by_email}</span>
                {revision.created_by_role && (
                  <Badge variant="outline" className="text-xs">
                    {revision.created_by_role}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <Clock className="w-3 h-3" />
                {formatDate(revision.created_at)}
              </div>
            </div>
            
            {/* Comment type badge */}
            <div className="mb-2">
              <Badge className={`${typeConfig.color} border`}>
                {typeConfig.label[language]}
              </Badge>
            </div>
            
            {/* State transition info */}
            {revision.previous_state && revision.new_state && (
              <div className="mb-2 p-2 bg-purple-50 rounded-lg text-sm">
                <span className="text-gray-600">
                  {WORKFLOW_STATES[revision.previous_state]?.[language] || revision.previous_state}
                </span>
                <span className="mx-2">→</span>
                <span className="font-medium text-purple-700">
                  {WORKFLOW_STATES[revision.new_state]?.[language] || revision.new_state}
                </span>
                {revision.ucl_reference && (
                  <span className="ml-2 text-gray-500">
                    (Ref: {revision.ucl_reference})
                  </span>
                )}
              </div>
            )}
            
            {/* Comment text */}
            {revision.comment && (
              <div className="text-sm text-gray-700">
                <p className={!expanded && shouldShowExpand ? 'line-clamp-3' : ''}>
                  {displayComment}
                  {!expanded && shouldShowExpand && '...'}
                </p>
                {shouldShowExpand && (
                  <button
                    onClick={() => setExpanded(!expanded)}
                    className="mt-1 text-blue-600 hover:underline flex items-center gap-1 text-xs"
                  >
                    {expanded ? (
                      <>
                        <ChevronUp className="w-3 h-3" />
                        {language === 'cs' ? 'Zobrazit méně' : 'Show less'}
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-3 h-3" />
                        {language === 'cs' ? 'Zobrazit více' : 'Show more'}
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
            
            {/* Attachments */}
            {revision.attachments && revision.attachments.length > 0 && (
              <div className="mt-2 pt-2 border-t">
                <p className="text-xs text-gray-500 mb-1">
                  {language === 'cs' ? 'Přílohy:' : 'Attachments:'}
                </p>
                <div className="flex flex-wrap gap-1">
                  {revision.attachments.map((att, idx) => (
                    <a
                      key={idx}
                      href={att.file_path}
                      className="text-xs text-blue-600 hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {att.file_name}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Filter chip component
const FilterChip = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1 text-xs rounded-full transition-colors ${
      active 
        ? 'bg-blue-600 text-white' 
        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
    }`}
  >
    {children}
  </button>
);

// Main timeline component
export const RevisionTimeline = ({ 
  authorizationId,
  language = 'cs',
  onRefresh
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeline, setTimeline] = useState(null);
  
  // Filters
  const [sortOrder, setSortOrder] = useState('newest');
  const [commentTypeFilter, setCommentTypeFilter] = useState(null);
  const [page, setPage] = useState(1);
  
  // Translations
  const t = {
    title: language === 'cs' ? 'Historie' : 'History',
    noEntries: language === 'cs' ? 'Zatím žádné záznamy' : 'No entries yet',
    loadMore: language === 'cs' ? 'Načíst další' : 'Load More',
    filter: {
      all: language === 'cs' ? 'Vše' : 'All',
      internal: language === 'cs' ? 'Interní' : 'Internal',
      ucl: language === 'cs' ? 'ÚCL' : 'ÚCL',
      operator: language === 'cs' ? 'Operátor' : 'Operator',
      system: language === 'cs' ? 'Systém' : 'System',
      transitions: language === 'cs' ? 'Přechody' : 'Transitions'
    },
    sort: {
      newest: language === 'cs' ? 'Nejnovější' : 'Newest first',
      oldest: language === 'cs' ? 'Nejstarší' : 'Oldest first'
    },
    currentState: language === 'cs' ? 'Aktuální stav' : 'Current State',
    error: language === 'cs' ? 'Chyba při načítání historie' : 'Error loading history'
  };
  
  // Fetch timeline
  const fetchTimeline = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let url = `/authorizations/${authorizationId}/revisions?sort_order=${sortOrder}&page=${page}`;
      if (commentTypeFilter) {
        url += `&comment_types=${commentTypeFilter}`;
      }
      
      const response = await api.get(url);
      setTimeline(response.data);
    } catch (err) {
      console.error('Failed to fetch timeline:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (authorizationId) {
      fetchTimeline();
    }
  }, [authorizationId, sortOrder, commentTypeFilter, page]);
  
  // Handle filter change
  const handleFilterChange = (type) => {
    setCommentTypeFilter(type === commentTypeFilter ? null : type);
    setPage(1);
  };
  
  if (loading && !timeline) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t.title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex gap-4">
              <Skeleton className="w-10 h-10 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-20 w-full" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }
  
  if (error) {
    return (
      <Card className="border-red-200">
        <CardContent className="p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-600">{t.error}</p>
          <Button 
            variant="outline" 
            size="sm" 
            className="mt-2"
            onClick={fetchTimeline}
          >
            {language === 'cs' ? 'Zkusit znovu' : 'Try again'}
          </Button>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card data-testid="revision-timeline">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5" />
            {t.title}
          </CardTitle>
          
          {/* Current state badge */}
          {timeline?.current_state && (
            <Badge variant="outline" className="text-sm">
              {t.currentState}: {WORKFLOW_STATES[timeline.current_state]?.[language] || timeline.current_state}
            </Badge>
          )}
        </div>
        
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <div className="flex items-center gap-1">
            <Filter className="w-4 h-4 text-gray-400" />
          </div>
          <FilterChip 
            active={!commentTypeFilter} 
            onClick={() => handleFilterChange(null)}
          >
            {t.filter.all}
          </FilterChip>
          <FilterChip 
            active={commentTypeFilter === 'internal_note'} 
            onClick={() => handleFilterChange('internal_note')}
          >
            {t.filter.internal}
          </FilterChip>
          <FilterChip 
            active={commentTypeFilter === 'ucl_feedback'} 
            onClick={() => handleFilterChange('ucl_feedback')}
          >
            {t.filter.ucl}
          </FilterChip>
          <FilterChip 
            active={commentTypeFilter === 'operator_response'} 
            onClick={() => handleFilterChange('operator_response')}
          >
            {t.filter.operator}
          </FilterChip>
          <FilterChip 
            active={commentTypeFilter === 'system_event'} 
            onClick={() => handleFilterChange('system_event')}
          >
            {t.filter.system}
          </FilterChip>
          <FilterChip 
            active={commentTypeFilter === 'status_transition'} 
            onClick={() => handleFilterChange('status_transition')}
          >
            {t.filter.transitions}
          </FilterChip>
          
          <div className="ml-auto">
            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger className="w-40 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">{t.sort.newest}</SelectItem>
                <SelectItem value="oldest">{t.sort.oldest}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-4">
        {timeline?.revisions?.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>{t.noEntries}</p>
          </div>
        ) : (
          <div className="space-y-0">
            {timeline?.revisions?.map((revision, index) => (
              <RevisionEntry
                key={revision.id}
                revision={revision}
                language={language}
                isLast={index === timeline.revisions.length - 1}
              />
            ))}
          </div>
        )}
        
        {/* Load more button */}
        {timeline?.has_more && (
          <div className="text-center mt-4">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={loading}
            >
              {loading ? '...' : t.loadMore}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RevisionTimeline;
